/**
 * Avatar Studio — Hub interne pour gérer la bibliothèque vidéo (Sora 2 + selfies).
 * Route admin : /app/avatar-studio
 *
 * 3 sections :
 *  - Génération Sora 2 (text-to-video b-roll)
 *  - Upload selfie (avatar humain réel)
 *  - Bibliothèque (liste, filtre par use case, supprimer)
 */
import { useEffect, useState, useRef } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Video, Upload, Sparkles, Trash2, Loader2, Film, Tag } from "lucide-react";

const USE_CASES = [
  { id: "cours_en_ligne", label: "📚 Cours en ligne" },
  { id: "powerkick_her", label: "🥊 PowerKick Her" },
  { id: "camille_receptionist", label: "📞 Camille AI" },
  { id: "marketing_general", label: "📣 Marketing général" },
];

const SIZES = ["1280x720", "1792x1024", "1024x1792", "1024x1024"];
const DURATIONS = [4, 8, 12];
const MODELS = [
  { id: "sora-2", label: "sora-2 (rapide)" },
  { id: "sora-2-pro", label: "sora-2-pro (qualité+)" },
];

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

export default function AvatarStudioModule() {
  const [tab, setTab] = useState("library");
  const [clips, setClips] = useState([]);
  const [stats, setStats] = useState(null);
  const [filterUseCase, setFilterUseCase] = useState("");
  const [filterKind, setFilterKind] = useState("");
  const [loading, setLoading] = useState(false);

  // Sora form
  const [soraForm, setSoraForm] = useState({
    prompt: "", title: "", model: "sora-2", size: "1280x720", duration: 4, use_case: "cours_en_ligne", notes: "",
  });
  const [generating, setGenerating] = useState(false);

  // Selfie upload
  const fileRef = useRef(null);
  const [selfieForm, setSelfieForm] = useState({ title: "", use_case: "cours_en_ligne", notes: "" });
  const [uploading, setUploading] = useState(false);

  const loadClips = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterUseCase) params.use_case = filterUseCase;
      if (filterKind) params.kind = filterKind;
      const r = await api.get("/avatar/clips", { params });
      setClips(r.data.clips);
      const s = await api.get("/avatar/library/stats");
      setStats(s.data);
    } catch { toast.error("Erreur bibliothèque"); }
    setLoading(false);
  };

  useEffect(() => { loadClips(); }, [filterUseCase, filterKind]);

  const generateSora = async () => {
    if (!soraForm.prompt.trim()) return toast.error("Prompt requis");
    setGenerating(true);
    toast.info("Génération Sora 2 en cours... 2-5 min. Reste sur cette page.");
    try {
      const r = await api.post("/avatar/sora/generate", soraForm, { timeout: 600_000 });
      toast.success(`Vidéo générée : ${r.data.clip.title}`);
      setSoraForm({ ...soraForm, prompt: "", title: "" });
      loadClips();
      setTab("library");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec génération Sora 2");
    }
    setGenerating(false);
  };

  const uploadSelfie = async () => {
    if (!fileRef.current?.files?.[0]) return toast.error("Sélectionne une vidéo");
    if (!selfieForm.title.trim()) return toast.error("Titre requis");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", fileRef.current.files[0]);
      fd.append("title", selfieForm.title);
      fd.append("use_case", selfieForm.use_case);
      if (selfieForm.notes) fd.append("notes", selfieForm.notes);
      await api.post("/avatar/selfie/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 600_000,
      });
      toast.success("Selfie uploadé !");
      setSelfieForm({ title: "", use_case: "cours_en_ligne", notes: "" });
      if (fileRef.current) fileRef.current.value = "";
      loadClips();
      setTab("library");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec upload");
    }
    setUploading(false);
  };

  const deleteClip = async (id) => {
    if (!window.confirm("Supprimer définitivement ce clip ?")) return;
    try {
      await api.delete(`/avatar/clips/${id}`);
      toast.success("Supprimé");
      loadClips();
    } catch { toast.error("Erreur suppression"); }
  };

  const [publishFor, setPublishFor] = useState(null);   // clip id en mode publication
  const [pubAccess, setPubAccess] = useState("membres");

  const publishCapsule = async (clip) => {
    try {
      const r = await api.post(`/avatar/clips/${clip.id}/publish`, {
        title: clip.title, access_level: pubAccess,
      });
      toast.success(`Capsule publiée sur /videos (${r.data.access_level})`);
      setPublishFor(null);
      loadClips();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur publication"); }
  };

  const unpublishCapsule = async (clip) => {
    try {
      await api.delete(`/avatar/clips/${clip.id}/publish`);
      toast.success("Capsule retirée de /videos");
      loadClips();
    } catch { toast.error("Erreur"); }
  };

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto" data-testid="avatar-studio-module">
      <div>
        <h1 className="text-3xl font-bold text-amber-100 tracking-tight flex items-center gap-2">
          <Video className="w-7 h-7 text-amber-400" /> Avatar Studio
        </h1>
        <p className="text-zinc-400 text-sm mt-1">
          Bibliothèque hybride : <strong>Sora 2</strong> (b-roll IA) + <strong>tes vidéos selfie</strong> (avatar humain réel).
          Réutilise pour cours en ligne, PowerKick Her, Camille AI.
        </p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-4">
            <div className="text-xs text-zinc-500 uppercase tracking-widest">Clips total</div>
            <div className="text-2xl font-bold text-amber-100 mt-1">{stats.total_clips}</div>
          </div>
          <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-4">
            <div className="text-xs text-zinc-500 uppercase tracking-widest">Sora 2</div>
            <div className="text-2xl font-bold text-purple-300 mt-1">{stats.by_kind.sora || 0}</div>
          </div>
          <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-4">
            <div className="text-xs text-zinc-500 uppercase tracking-widest">Selfies</div>
            <div className="text-2xl font-bold text-emerald-300 mt-1">{stats.by_kind.selfie || 0}</div>
          </div>
          <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-4">
            <div className="text-xs text-zinc-500 uppercase tracking-widest">Stockage</div>
            <div className="text-2xl font-bold text-amber-100 mt-1">{stats.storage_used_mb} MB</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-zinc-800">
        {[
          { id: "library", label: "📚 Bibliothèque" },
          { id: "sora", label: "✨ Générer Sora 2" },
          { id: "selfie", label: "📹 Upload selfie" },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
              tab === t.id ? "border-amber-500 text-amber-300" : "border-transparent text-zinc-400 hover:text-amber-200"
            }`}
            data-testid={`tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* SORA */}
      {tab === "sora" && (
        <div className="space-y-4 max-w-2xl">
          <div className="rounded-2xl border border-purple-500/30 bg-gradient-to-br from-purple-500/10 to-zinc-950 p-5">
            <h3 className="text-md font-semibold text-purple-200 mb-3 flex items-center gap-2">
              <Sparkles className="w-4 h-4" /> Génération vidéo IA — Sora 2
            </h3>
            <div className="space-y-3">
              <textarea placeholder="Décris la scène à générer (anglais préférable pour Sora). Ex: 'A confident woman wearing boxing gloves, training intensely in a dimly lit gym, slow motion, cinematic lighting.'"
                value={soraForm.prompt}
                onChange={(e) => setSoraForm({ ...soraForm, prompt: e.target.value })}
                className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-amber-100 min-h-[100px]"
                data-testid="sora-prompt" />
              <input placeholder="Titre du clip (optionnel)" value={soraForm.title}
                onChange={(e) => setSoraForm({ ...soraForm, title: e.target.value })}
                className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-amber-100"
                data-testid="sora-title" />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <select value={soraForm.model} onChange={(e) => setSoraForm({ ...soraForm, model: e.target.value })}
                  className="bg-zinc-900 border border-zinc-700 rounded-lg px-2 py-2 text-xs text-amber-100" data-testid="sora-model">
                  {MODELS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
                </select>
                <select value={soraForm.size} onChange={(e) => setSoraForm({ ...soraForm, size: e.target.value })}
                  className="bg-zinc-900 border border-zinc-700 rounded-lg px-2 py-2 text-xs text-amber-100" data-testid="sora-size">
                  {SIZES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <select value={soraForm.duration} onChange={(e) => setSoraForm({ ...soraForm, duration: parseInt(e.target.value, 10) })}
                  className="bg-zinc-900 border border-zinc-700 rounded-lg px-2 py-2 text-xs text-amber-100" data-testid="sora-duration">
                  {DURATIONS.map(d => <option key={d} value={d}>{d}s</option>)}
                </select>
                <select value={soraForm.use_case} onChange={(e) => setSoraForm({ ...soraForm, use_case: e.target.value })}
                  className="bg-zinc-900 border border-zinc-700 rounded-lg px-2 py-2 text-xs text-amber-100" data-testid="sora-usecase">
                  {USE_CASES.map(u => <option key={u.id} value={u.id}>{u.label}</option>)}
                </select>
              </div>
              <button onClick={generateSora} disabled={generating}
                className="w-full px-4 py-3 rounded-lg bg-purple-500 hover:bg-purple-400 disabled:opacity-50 text-zinc-950 font-semibold text-sm flex items-center justify-center gap-2"
                data-testid="generate-sora-btn">
                {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Génération (2-5 min)...</> : <><Sparkles className="w-4 h-4" /> Générer la vidéo</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SELFIE UPLOAD */}
      {tab === "selfie" && (
        <div className="space-y-4 max-w-2xl">
          <div className="rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 to-zinc-950 p-5">
            <h3 className="text-md font-semibold text-emerald-200 mb-3 flex items-center gap-2">
              <Upload className="w-4 h-4" /> Upload vidéo selfie (MP4/MOV/WebM)
            </h3>
            <div className="space-y-3">
              <input type="file" ref={fileRef} accept="video/mp4,video/quicktime,video/webm"
                className="w-full text-xs text-zinc-300 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-emerald-500 file:text-zinc-950 file:font-semibold hover:file:bg-emerald-400"
                data-testid="selfie-file" />
              <input placeholder="Titre du clip *" value={selfieForm.title}
                onChange={(e) => setSelfieForm({ ...selfieForm, title: e.target.value })}
                className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-amber-100"
                data-testid="selfie-title" />
              <select value={selfieForm.use_case} onChange={(e) => setSelfieForm({ ...selfieForm, use_case: e.target.value })}
                className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-amber-100" data-testid="selfie-usecase">
                {USE_CASES.map(u => <option key={u.id} value={u.id}>{u.label}</option>)}
              </select>
              <textarea placeholder="Notes (optionnel)" value={selfieForm.notes}
                onChange={(e) => setSelfieForm({ ...selfieForm, notes: e.target.value })}
                className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-amber-100" />
              <button onClick={uploadSelfie} disabled={uploading}
                className="w-full px-4 py-3 rounded-lg bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-zinc-950 font-semibold text-sm flex items-center justify-center gap-2"
                data-testid="upload-selfie-btn">
                {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Upload...</> : <><Upload className="w-4 h-4" /> Téléverser</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* LIBRARY */}
      {tab === "library" && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-2 items-center">
            <Tag className="w-4 h-4 text-zinc-500" />
            <select value={filterKind} onChange={(e) => setFilterKind(e.target.value)}
              className="bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-1.5 text-xs text-amber-100" data-testid="filter-kind">
              <option value="">Tous types</option>
              <option value="sora">Sora 2</option>
              <option value="selfie">Selfies</option>
            </select>
            <select value={filterUseCase} onChange={(e) => setFilterUseCase(e.target.value)}
              className="bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-1.5 text-xs text-amber-100" data-testid="filter-usecase">
              <option value="">Tous usages</option>
              {USE_CASES.map(u => <option key={u.id} value={u.id}>{u.label}</option>)}
            </select>
          </div>

          {loading && <Loader2 className="w-6 h-6 animate-spin text-amber-400 mx-auto" />}

          {clips.length === 0 && !loading && (
            <div className="text-center text-zinc-500 py-12">
              <Film className="w-10 h-10 mx-auto mb-3 opacity-50" />
              Aucun clip pour l'instant. Génère ta première vidéo Sora 2 ou téléverse un selfie.
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {clips.map(c => (
              <div key={c.id} className="rounded-2xl border border-zinc-800 bg-zinc-950 overflow-hidden" data-testid={`clip-${c.id}`}>
                <video controls preload="metadata" className="w-full h-44 bg-zinc-900 object-contain">
                  <source src={`${BACKEND}${c.file_url}`} type="video/mp4" />
                </video>
                <div className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-sm font-semibold text-amber-100 truncate">{c.title}</div>
                    <button onClick={() => deleteClip(c.id)} className="text-zinc-500 hover:text-rose-400" data-testid={`del-${c.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="text-[10px] text-zinc-500 mt-1 flex items-center gap-2 flex-wrap">
                    <span className={`px-2 py-0.5 rounded-full ${c.kind === "sora" ? "bg-purple-500/20 text-purple-300" : "bg-emerald-500/20 text-emerald-300"}`}>{c.kind}</span>
                    <span className="px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-400">{c.use_case}</span>
                    {c.duration_sec && <span>{c.duration_sec}s</span>}
                    {c.file_size_mb && <span>{c.file_size_mb} MB</span>}
                  </div>
                  {c.prompt && <div className="text-[10px] text-zinc-500 mt-2 italic line-clamp-2">"{c.prompt}"</div>}

                  {/* AVATAR V2 — Publication en capsule vidéo (/videos) */}
                  <div className="mt-3 pt-3 border-t border-zinc-900">
                    {c.published_video_id ? (
                      <div className="flex items-center justify-between gap-2" data-testid={`capsule-published-${c.id}`}>
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 flex items-center gap-1">
                          <Film className="w-3 h-3" /> Capsule · {c.capsule_access || "membres"}
                        </span>
                        <div className="flex items-center gap-2">
                          <a href="/videos" target="_blank" rel="noreferrer" className="text-[10px] text-zinc-400 underline">Voir</a>
                          <button onClick={() => unpublishCapsule(c)} className="text-[10px] text-rose-400 hover:text-rose-300" data-testid={`unpublish-${c.id}`}>Retirer</button>
                        </div>
                      </div>
                    ) : publishFor === c.id ? (
                      <div className="flex items-center gap-2" data-testid={`publish-panel-${c.id}`}>
                        <select value={pubAccess} onChange={(e) => setPubAccess(e.target.value)}
                          className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-[11px] text-amber-100 flex-1" data-testid={`pub-access-${c.id}`}>
                          <option value="public">Public (gratuit)</option>
                          <option value="membres">Membres</option>
                          <option value="premium">Premium 👑</option>
                        </select>
                        <button onClick={() => publishCapsule(c)} className="text-[11px] bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold px-3 py-1 rounded" data-testid={`confirm-publish-${c.id}`}>Publier</button>
                        <button onClick={() => setPublishFor(null)} className="text-[11px] text-zinc-500">✕</button>
                      </div>
                    ) : (
                      <button onClick={() => { setPublishFor(c.id); setPubAccess("membres"); }}
                        className="w-full text-[11px] border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 rounded py-1.5 flex items-center justify-center gap-1.5"
                        data-testid={`publish-capsule-${c.id}`}>
                        <Film className="w-3 h-3" /> Publier en capsule vidéo
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
